import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Container,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from "@mui/icons-material";
import { onSnapshot, query, orderBy } from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";
import CallRecordingPlayer from "./CallRecordingPlayer";

// Format a duration in seconds as "Mm Ss"
const formatDuration = (seconds) => {
  const s = parseInt(seconds, 10) || 0;
  if (s === 0) return "—";
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// Format a US phone number for display; falls back to the raw value
const formatPhone = (raw) => {
  if (!raw) return "Unknown";
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
  if (ten.length === 10) {
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return raw;
};

// Convert a Firestore Timestamp / Date / string to a JS Date
const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (date) =>
  date
    ? date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const COL_COUNT = 8;

const CallRow = ({ row, open, onToggle }) => {
  const hasRecording = Boolean(row.recordingUrl);

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 48 }}>
          <Tooltip title={hasRecording ? (open ? "Hide recording" : "Show recording") : "No recording"}>
            <span>
              <IconButton size="small" onClick={() => onToggle(row.id)} disabled={!hasRecording}>
                {open ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            label={row.status}
            color={row.status === "Answered" ? "success" : "error"}
            size="small"
          />
        </TableCell>
        <TableCell>{formatDateTime(row.callDate)}</TableCell>
        <TableCell>{formatPhone(row.from)}</TableCell>
        <TableCell>{row.answeredBy || "—"}</TableCell>
        <TableCell>{row.listName || "—"}</TableCell>
        <TableCell>{formatDuration(row.durationSeconds)}</TableCell>
        <TableCell align="center">
          <Tooltip title={hasRecording ? "Play recording" : "No recording"}>
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onToggle(row.id)}
                disabled={!hasRecording}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={COL_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1, bgcolor: "action.hover", borderRadius: 1 }}>
              {hasRecording ? (
                <CallRecordingPlayer url={row.recordingUrl} dualChannel={row.dualChannel === true} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No recording available for this call.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CallHistory = ({ clientId: propClientId, selectedProperty: propSelectedProperty }) => {
  const { selectedProperty: contextProperty, selectedClient: contextClient } = useContext(PropertyContext);
  const selectedProperty = propSelectedProperty || contextProperty;
  const clientId = propClientId || contextClient;

  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Real-time listener for call history
  useEffect(() => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }

    const callsRef = propertyCollection(clientId, selectedProperty, "callHistory");
    const q = query(callsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            callDate: toDate(d.startTime) || toDate(d.timestamp),
          };
        });
        setCalls(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching call history:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedProperty]);

  // Filter by status and search
  useEffect(() => {
    let filtered = calls;

    if (statusFilter !== "All") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.replace(/\D/g, "");
      const text = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (c) =>
          (term && (c.from || "").replace(/\D/g, "").includes(term)) ||
          (c.answeredBy || "").toLowerCase().includes(text) ||
          (c.listName || "").toLowerCase().includes(text)
      );
    }

    setFilteredCalls(filtered);
    setPage(0);
  }, [calls, searchTerm, statusFilter]);

  const handleToggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const stats = {
    total: calls.length,
    answered: calls.filter((c) => c.status === "Answered").length,
    noAnswer: calls.filter((c) => c.status === "No Answer").length,
  };

  const pagedCalls = filteredCalls.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (!selectedProperty) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Please select a property to view call history.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">After-Hours Call History</Typography>
        <Typography variant="body2" color="text.secondary">
          Escalation calls routed through your guest call lists. Expand a row to play the recording —
          the guest is the top waveform, the contact who answered is the bottom.
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, p: 2, bgcolor: "primary.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="primary.main">
            {stats.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Calls
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, bgcolor: "success.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="success.main">
            {stats.answered}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Answered
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, bgcolor: "error.lighter", borderRadius: 1, textAlign: "center" }}>
          <Typography variant="h4" color="error.main">
            {stats.noAnswer}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No Answer
          </Typography>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Phone number, name, or list"
          sx={{ flex: 1, minWidth: 250 }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            label="All"
            color={statusFilter === "All" ? "primary" : "default"}
            onClick={() => setStatusFilter("All")}
            clickable
          />
          <Chip
            label="Answered"
            color={statusFilter === "Answered" ? "success" : "default"}
            onClick={() => setStatusFilter("Answered")}
            clickable
          />
          <Chip
            label="No Answer"
            color={statusFilter === "No Answer" ? "error" : "default"}
            onClick={() => setStatusFilter("No Answer")}
            clickable
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Status</TableCell>
              <TableCell>Date / Time</TableCell>
              <TableCell>Caller</TableCell>
              <TableCell>Answered By</TableCell>
              <TableCell>Escalation List</TableCell>
              <TableCell>Length</TableCell>
              <TableCell align="center">Recording</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : pagedCalls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No escalation calls yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedCalls.map((row) => (
                <CallRow key={row.id} row={row} open={openId === row.id} onToggle={handleToggle} />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredCalls.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Container>
  );
};

export default CallHistory;
